import MapNA from '/img/maps/map-na.svg'
import MapEU from '/img/maps/map-europe.svg'
import ICGIcon from '/img/maps/icg-icon.svg'

import { Text } from '#app/components/ui/typography.tsx'
import { type MouseEventHandler, useEffect, useRef, useState } from 'react'
import { cn, getStoreImageSrc } from '#app/utils/misc.tsx'
import {
	type IShowroomLocation,
	type IStoreWithImage,
	type MapType,
} from '#app/components/showrooms/types.ts'
import StoreHoverItem from '#app/components/showrooms/store-hover.tsx'
import { useIsMobile } from '#app/hooks/use-mobile.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { AccordionItem } from '@radix-ui/react-accordion'
import { useLocale } from '#app/utils/i18n.tsx'
import {
	StoreDetails,
	StoreTitle,
} from '#app/components/showrooms/store-info.tsx'
import { StoreType } from '#app/enums/store-type.ts'
import { useMouseMove } from '#app/hooks/use-mouse-move.ts'
import { useMatches } from 'react-router'

export interface MousePosition {
	x: number
	y: number
	percentX: number
	percentY: number
}

const ICON_BASE_SIZE_PX = 16
const MAP_BASE_SIZE_PX = 400

export default function StoreMapLocationChooser({
	store,
	stores,
	editable,
	onChoose,
	className,
}: {
	className?: string
	store?: IStoreWithImage
	stores: IStoreWithImage[]
} & (
	| {
			editable: true
			onChoose: (location: IShowroomLocation) => void
	  }
	| {
			editable: false
			onChoose?: never
	  }
)) {
	const [selected, setSelected] = useState<MapType>('eu')
	const mapRef = useRef<HTMLDivElement | null>(null)
	const [mousePos, handleMouseMove] = useMouseMove(mapRef)

	const isInAdmin = useMatches().some((match) => match.id.includes('admin'))

	const locale = useLocale()
	const isMobile = useIsMobile()

	const [storeLocation, setStoreLocation] = useState<IShowroomLocation | null>(
		store
			? {
					relativeMapPositionX: store.relativeMapPositionX,
					relativeMapPositionY: store.relativeMapPositionY,
					map: store.map as MapType,
					baseMapHeight: store.baseMapHeight,
					baseMapWidth: store.baseMapWidth,
				}
			: null,
	)
	const [mapRect, setMapRect] = useState<DOMRect | null>(null)

	useEffect(() => {
		const map = mapRef.current
		if (!map) return

		const initialRect = map.getBoundingClientRect()
		setMapRect(initialRect)

		const resizeObserver = new ResizeObserver(() => {
			const rect = map.getBoundingClientRect()

			if (rect !== initialRect) setMapRect(rect)
		})

		resizeObserver.observe(map)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	const handleClick = () => {
		const map = mapRef.current
		if (!map) return
		const mapRect = map.getBoundingClientRect()

		const newLocation = {
			map: selected,
			baseMapWidth: mapRect.width,
			baseMapHeight: mapRect.height,
			relativeMapPositionX: mousePos.percentX,
			relativeMapPositionY: mousePos.percentY,
		}

		setStoreLocation(newLocation)
		if (onChoose) onChoose(newLocation)
	}

	const handleMapChange = (map: MapType) => {
		if (selected === map) return
		setStoreLocation(null)
		setSelected(map)
	}

	return (
		<div className={cn('flex flex-col gap-sm', className)}>
			<Text as={'div'} className={'tracking-widest'}>
				/{' '}
				<span
					onClick={() => {
						handleMapChange('na')
					}}
					className={cn(
						selected === 'na' && 'font-semibold',
						selected !== 'na' && 'cursor-pointer',
					)}
				>
					norteamerica
				</span>{' '}
				/{' '}
				<span
					onClick={() => {
						handleMapChange('eu')
					}}
					className={cn(
						selected === 'eu' && 'font-semibold',
						selected !== 'eu' && 'cursor-pointer',
					)}
				>
					europa
				</span>{' '}
				/
			</Text>
			{editable && (
				<Text as={'p'} weight={'light'}>
					Haz click lo más próximo posible a donde esté la tienda. Se guardará
					la posición y será la que se muestre en la página de contacto. Intenta
					que no se superpongan tiendas.
				</Text>
			)}

			{editable && process.env.NODE_ENV !== 'production' && (
				<div className="mb-4 rounded bg-white p-4">
					<div className="grid grid-cols-2 gap-4 font-mono text-sm">
						<div>
							<span className="font-semibold">Pixels:</span> (
							{mousePos.x.toFixed(0)}, {mousePos.y.toFixed(0)})
						</div>
						<div>
							<span className="font-semibold">Percentage:</span> (
							{mousePos.percentX.toFixed(1)}%, {mousePos.percentY.toFixed(1)}%)
						</div>
					</div>
				</div>
			)}

			{!mapRect && (
				<div className="mb-4 rounded bg-white p-4">
					<div className="grid font-mono text-sm">
						El mapa no se ha terminado de cargar bien.
					</div>
				</div>
			)}

			<div
				ref={mapRef}
				className={'relative'}
				onMouseMove={
					editable
						? (handleMouseMove as unknown as MouseEventHandler)
						: undefined
				}
				onClick={editable ? handleClick : undefined}
			>
				{mapRect && storeLocation && (
					<img
						src={ICGIcon}
						alt={'Icono de tienda'}
						className={'absolute -translate-x-1/2 -translate-y-1/2'}
						style={{
							height: (mapRect!.height * ICON_BASE_SIZE_PX) / MAP_BASE_SIZE_PX,
							width: (mapRect!.height * ICON_BASE_SIZE_PX) / MAP_BASE_SIZE_PX,
							top:
								(mapRect!.height *
									(storeLocation.baseMapHeight *
										(storeLocation.relativeMapPositionY / 100))) /
								storeLocation.baseMapHeight,
							left:
								(mapRect!.width *
									(storeLocation.baseMapWidth *
										(storeLocation.relativeMapPositionX / 100))) /
								storeLocation.baseMapWidth,
						}}
					/>
				)}

				{mapRect &&
					stores
						// Filter out the current store for editing purposes.
						.filter((s) => {
							if (store && s.id === store.id) {
								return false
							}

							return true
						})
						// Only show those who are supposed to be on this map.
						.filter((s) => s.map === selected)
						.map((s) => (
							<StoreHoverItem
								key={s.id}
								store={s}
								className={'absolute -translate-x-1/2 -translate-y-1/2'}
								style={{
									height:
										(mapRect!.height * ICON_BASE_SIZE_PX) / MAP_BASE_SIZE_PX,
									width:
										(mapRect!.height * ICON_BASE_SIZE_PX) / MAP_BASE_SIZE_PX,
									top:
										(mapRect!.height *
											(s.baseMapHeight * (s.relativeMapPositionY / 100))) /
										s.baseMapHeight,
									left:
										(mapRect!.width *
											(s.baseMapWidth * (s.relativeMapPositionX / 100))) /
										s.baseMapWidth,
								}}
							>
								<img
									src={ICGIcon}
									alt={'Icono de tienda'}
									className={cn('', editable && 'opacity-30')}
								/>
							</StoreHoverItem>
						))}
				{selected === 'eu' && <img src={MapEU} alt="Mapa Europeo" />}
				{selected === 'na' && <img src={MapNA} alt={'Mapa Norteamericano'} />}
			</div>

			{isMobile && !isInAdmin && !editable && (
				<Accordion type="single" collapsible>
					{stores
						// Filter out the current store for editing purposes.
						.filter((s) => {
							if (store && s.id === store.id) {
								return false
							}

							return true
						})
						// Only show those who are supposed to be on this map.
						.filter((s) => s.map === selected)
						.map((s) => (
							<AccordionItem key={s.id} value={s.id}>
								<AccordionTrigger>
									<StoreTitle store={s} />
								</AccordionTrigger>
								<AccordionContent
									className={cn(
										'mt-sm flex gap-sm lg:gap-lg',
										s.link && 'cursor-pointer',
									)}
									onClick={() => {
										if (s.link) {
											const a = window.document.createElement('a')
											a.href = s.link
											a.target = '_blank'
											document.body.appendChild(a).click()
										}
									}}
								>
									<img
										src={getStoreImageSrc(s.storeImageId)}
										className={
											'h-[min(60vw,_400px)] w-[min(40vw,_300px)] object-cover'
										}
										alt={StoreType.getByCode(s.type)?.labels[locale]}
									/>
									<StoreDetails store={s} />
								</AccordionContent>
							</AccordionItem>
						))}
				</Accordion>
			)}
		</div>
	)
}
